import { GreaterAreaDropdown, ThemeTypeDropdown, TourTypeDropdown } from '@/components/dropdowns';
import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import type { Tour } from '@/config/objects/tours';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EntityFormShell,
  FormFieldGroup,
  Input,
  RichTextEditor,
  SwitchField,
  toast,
  useEntityForm,
} from '@startupquickstart/frontend';
import { useNavigate, useParams } from 'react-router-dom';

interface TourFormData extends Record<string, unknown> {
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  tagline: string;
  description: string;
  featuredImageId: string | undefined;
  tourTypeId: string;
  greaterAreaId: string;
  defaultThemeTypeId: string;
  duration: string;
  stops: string;
  difficulty: string;
  estimatedWalking: string;
  highlights: string;
}

const DEFAULTS: TourFormData = {
  name: '',
  status: 'published',
  tagline: '',
  description: '',
  featuredImageId: undefined,
  tourTypeId: '',
  greaterAreaId: '',
  defaultThemeTypeId: '',
  duration: '',
  stops: '',
  difficulty: 'Easy',
  estimatedWalking: '',
  highlights: '',
};

export function TourFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const form = useEntityForm<TourFormData, Tour>({
    resource: 'tours',
    itemId,
    defaults: DEFAULTS,
    queryKey: 'Tour',
    fromApi: (t) => ({
      name: t.name ?? '',
      status: t.status ?? 'published',
      tagline: t.tagline ?? '',
      description: t.description ?? '',
      featuredImageId: t.featuredImageId ?? undefined,
      tourTypeId: t.tourTypeId ?? '',
      greaterAreaId: t.greaterAreaId ?? '',
      defaultThemeTypeId: t.defaultThemeTypeId ?? '',
      duration: t.duration ?? '',
      stops: t.stops != null ? String(t.stops) : '',
      difficulty: t.difficulty ?? 'Easy',
      estimatedWalking: t.estimatedWalking ?? '',
      highlights: t.highlights?.join('\n') ?? '',
    }),
    toApi: (f) => ({
      name: f.name.trim(),
      status: f.status,
      tagline: f.tagline.trim() || null,
      description: f.description || null,
      featuredImageId: f.featuredImageId || null,
      tourTypeId: f.tourTypeId || null,
      greaterAreaId: f.greaterAreaId || null,
      defaultThemeTypeId: f.defaultThemeTypeId || null,
      duration: f.duration.trim() || null,
      stops: f.stops ? parseInt(f.stops, 10) : undefined,
      difficulty: f.difficulty || null,
      estimatedWalking: f.estimatedWalking.trim() || null,
      highlights: f.highlights.trim()
        ? f.highlights.split('\n').map((h) => h.trim()).filter(Boolean)
        : undefined,
    }),
    validate: (f) => {
      const errs: Partial<Record<keyof TourFormData, string>> = {};
      if (!f.name.trim()) errs.name = 'Name is required';
      else if (f.name.trim().length < 2) errs.name = 'Name must be at least 2 characters';
      if (!f.tourTypeId) errs.tourTypeId = 'Tour type is required';
      if (f.tagline.trim() && f.tagline.trim().length < 5)
        errs.tagline = 'Tagline must be at least 5 characters';
      return errs;
    },
    onSuccess: (entity, { isEdit: wasEdit }) => {
      if (wasEdit) {
        toast.success('Tour saved successfully');
        navigate(-1);
      } else {
        toast.success('Tour created successfully');
        if (entity.id) navigate(`/tours/${entity.id}`);
      }
    },
    onNotFound: () => {
      toast.error('Tour not found');
      navigate('/tours');
    },
    onError: (err) => {
      // Auto-mapping handles ApiError.details → field errors; surface a toast
      // summarizing the bad fields on top of it.
      toast.error(err?.message || 'Failed to save tour');
    },
  });

  return (
    <EntityFormShell
      title={isEdit ? 'Edit Tour' : 'Create Tour'}
      subtitle={
        isEdit
          ? form.formData.name || 'Update tour details'
          : 'Add a new tour to the platform'
      }
      backHref={isEdit && itemId ? `/tours/${itemId}` : '/tours'}
      form={form}
    >
      <div className="space-y-6">
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">General Information</CardTitle>
            <CardDescription>Core tour details</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <FormFieldGroup name="name" label="Name" required error={form.errors.name}>
              <Input
                id="name"
                value={form.formData.name}
                onChange={(e) => form.updateField('name', e.target.value)}
                placeholder="Enter tour name"
                className={cn(form.errors.name && 'border-destructive')}
              />
            </FormFieldGroup>

            <FormFieldGroup name="tagline" label="Tagline" error={form.errors.tagline}>
              <Input
                id="tagline"
                value={form.formData.tagline}
                onChange={(e) => form.updateField('tagline', e.target.value)}
                placeholder="Enter compelling tagline"
                className={cn(form.errors.tagline && 'border-destructive')}
              />
            </FormFieldGroup>

            <ImageUploadDropzone
              targetAspectRatio={4 / 3}
              value={form.formData.featuredImageId}
              onChange={(value) => form.updateField('featuredImageId', value)}
            />

            <SwitchField
              label="Published"
              description="Make this tour visible to users"
              checked={form.formData.status === 'published'}
              onCheckedChange={(checked) =>
                form.updateField('status', checked ? 'published' : 'draft')
              }
            />

            <FormFieldGroup name="description" label="Description">
              <RichTextEditor
                value={form.formData.description}
                onChange={(value) => form.updateField('description', value ?? '')}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Classification</CardTitle>
            <CardDescription>Tour type, location, and theme</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup
                name="tourTypeId"
                label="Tour Type"
                required
                error={form.errors.tourTypeId}
              >
                <TourTypeDropdown
                  value={form.formData.tourTypeId}
                  onChange={(value) => form.updateField('tourTypeId', value)}
                  placeholder="Select Tour Type"
                />
              </FormFieldGroup>

              <FormFieldGroup name="greaterAreaId" label="Greater Area">
                <GreaterAreaDropdown
                  value={form.formData.greaterAreaId}
                  onChange={(value) => form.updateField('greaterAreaId', value)}
                  placeholder="Select Greater Area"
                />
              </FormFieldGroup>
            </div>

            <FormFieldGroup
              name="defaultThemeTypeId"
              label="Default Theme Type"
              description="Choose a theme for this tour. Attractions will use this theme when available."
            >
              <ThemeTypeDropdown
                value={form.formData.defaultThemeTypeId}
                onChange={(value) => form.updateField('defaultThemeTypeId', value)}
                placeholder="Select Theme Type"
              />
            </FormFieldGroup>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Tour Details</CardTitle>
            <CardDescription>Duration, difficulty and logistics</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup name="duration" label="Duration">
                <Input
                  id="duration"
                  value={form.formData.duration}
                  onChange={(e) => form.updateField('duration', e.target.value)}
                  placeholder="e.g., 1-2 hours"
                />
              </FormFieldGroup>

              <FormFieldGroup name="stops" label="Stops">
                <Input
                  id="stops"
                  type="number"
                  min="0"
                  value={form.formData.stops}
                  onChange={(e) => form.updateField('stops', e.target.value)}
                  placeholder="Number of stops"
                />
              </FormFieldGroup>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup name="difficulty" label="Difficulty">
                <select
                  id="difficulty"
                  value={form.formData.difficulty}
                  onChange={(e) => form.updateField('difficulty', e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="Easy">Easy</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Challenging">Challenging</option>
                </select>
              </FormFieldGroup>

              <FormFieldGroup name="estimatedWalking" label="Estimated Walking">
                <Input
                  id="estimatedWalking"
                  value={form.formData.estimatedWalking}
                  onChange={(e) => form.updateField('estimatedWalking', e.target.value)}
                  placeholder="e.g., 0 miles"
                />
              </FormFieldGroup>
            </div>

            <FormFieldGroup
              name="highlights"
              label="Highlights"
              description="Enter each highlight on a new line"
            >
              <textarea
                id="highlights"
                value={form.formData.highlights}
                onChange={(e) => form.updateField('highlights', e.target.value)}
                placeholder="Enter highlights separated by new lines"
                rows={4}
                className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </FormFieldGroup>
          </CardContent>
        </Card>
      </div>
    </EntityFormShell>
  );
}
